/**
 * StreakFlame — compact header badge showing the user's current journal streak.
 * Clicking it deep-links to /pkm?tab=journal so the habit loop stays one click away.
 * Hides itself if the user has zero entries (no noise for non-journal users).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Flame } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StreakFlame = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  const fetch = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/pkm/journal-stats/streak`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(r.data);
    } catch {
      /* silent — widget just hides */
    }
  }, [token]);

  useEffect(() => { fetch(); }, [fetch]);
  // Refresh when tab regains focus so the badge stays in sync after journaling
  useEffect(() => {
    const handler = () => fetch();
    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, [fetch]);

  // Hide if user has never journaled
  if (!stats || stats.total_entries === 0) return null;

  const current = stats.current_streak || 0;
  const loggedToday = stats.today_logged;
  const color = loggedToday ? '#FF9500' : '#9CA3AF';
  const tooltip = loggedToday
    ? `${current}-day streak · logged today`
    : `${current}-day streak · log today to keep it alive`;

  return (
    <button
      type="button"
      onClick={() => navigate('/pkm?tab=journal')}
      title={tooltip}
      className={`flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-semibold transition-all hover:scale-105 ${
        loggedToday ? 'bg-[#FF9500]/10 text-[#FF9500] hover:bg-[#FF9500]/15' : 'bg-[#F3F4F6] text-[#9CA3AF] hover:bg-[#E5E7EB]'
      }`}
      data-testid="streak-flame-badge"
    >
      <Flame className="w-3.5 h-3.5" style={{ color }} fill={loggedToday ? color : 'none'} />
      <span data-testid="streak-flame-count">{current}</span>
      {!loggedToday && current > 0 && <span className="text-[10px] opacity-60 hidden sm:inline">· log today</span>}
    </button>
  );
};

export default StreakFlame;
